import React from 'react';
import { ConfigProvider } from 'antd';
import { FiClock, FiUsers, FiCheck, FiGift, FiZap, FiAlertTriangle, FiArrowRight } from 'react-icons/fi';

const DIA_SHORT = { lunes:'Lu', martes:'Ma', miércoles:'Mi', jueves:'Ju', viernes:'Vi', sábado:'Sa', domingo:'Do' };
const diaShort  = (d) => DIA_SHORT[d?.toLowerCase()] || d?.slice(0, 2) || '';

export default function PasoHorarios({ taller, esGratis, selectedHorarios, toggleHorario, descuentosDisponibles, onNext }) {

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#0047bb' } }}>
      <div className="space-y-5">

        {/* Aviso gratis */}
        {esGratis && (
          <div className="flex items-center gap-3 p-4 rounded-2xl" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
            <FiGift size={20} className="text-blue-600 flex-shrink-0" />
            <div>
              <p className="font-bold text-blue-800 text-sm">¡Taller gratuito!</p>
              <p className="text-blue-600 text-[12px]">No requiere pago — solo confirma tu inscripción</p>
            </div>
          </div>
        )}

        {/* Descuentos */}
        {descuentosDisponibles?.length > 0 && !esGratis && (
          <div className="p-4 rounded-2xl" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
            <div className="flex items-center gap-2 mb-2">
              <FiZap size={16} className="text-yellow-600" />
              <p className="font-bold text-yellow-800 text-sm">¡Descuentos disponibles!</p>
            </div>
            <div className="space-y-1">
              {descuentosDisponibles.map((d, i) => (
                <div key={i} className="flex items-center gap-2 text-[12px] text-yellow-700">
                  <span className="font-bold">
                    {d.tipoDescuento === 'porcentaje' ? `${d.valorDescuento}% OFF` : `-$${d.valorDescuento?.toLocaleString('es-CL')}`}
                  </span>
                  <span>· {d.nombre}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rango de edades */}
        {(taller.edadMin || taller.edadMax) && (
          <div className="flex items-center gap-3 p-3.5 rounded-2xl bg-blue-50 border border-blue-100">
            <FiUsers size={16} className="text-blue-600 flex-shrink-0" />
            <p className="text-[13px] text-blue-700 font-medium">
              {taller.edadMin && taller.edadMax
                ? `Dirigido a personas de ${taller.edadMin} a ${taller.edadMax} años`
                : taller.edadMin
                  ? `Para mayores de ${taller.edadMin} años`
                  : `Hasta ${taller.edadMax} años`}
            </p>
          </div>
        )}

        {/* Instrucción */}
        <p className="text-[13px] font-semibold text-gray-500">
          Selecciona los horarios en los que deseas inscribirte:
        </p>

        {/* Lista de horarios */}
        <div className="space-y-3">
          {taller.horarios?.length > 0 ? taller.horarios.map(h => {
            const ilimitado  = h.cuposIlimitados;
            const total      = h.cupos || 0;
            const ocupados   = h.cuposOcupados   || 0;
            const reservados = h.cuposReservados || 0;
            const disp       = ilimitado ? Infinity : total - ocupados - reservados;
            const disponible = ilimitado || disp > 0;
            const sobrecupo  = !ilimitado && ocupados > total;
            const sel        = !!selectedHorarios.find(x => x._id === h._id);
            const pct        = !ilimitado && total > 0 ? Math.min(Math.round((ocupados / total) * 100), 100) : 0;
            const barColor   = sobrecupo ? '#7f1d1d' : pct >= 90 ? '#ef4444' : pct >= 65 ? '#f97316' : '#0047bb';

            return (
              <button key={h._id} type="button"
                onClick={() => disponible && toggleHorario(h)}
                disabled={!disponible}
                className={`horario-card w-full text-left rounded-2xl p-4 transition-all
                  ${sel ? 'selected' : ''} ${!disponible ? 'disabled opacity-50 cursor-not-allowed' : ''}`}
                style={{ background: '#fff', border: '2px solid', borderColor: sel ? '#0047bb' : '#e5e7eb' }}
              >
                <div className="flex items-start gap-3">
                  {/* Checkbox visual */}
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all
                    ${sel ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                    {sel && <FiCheck size={11} className="text-white" strokeWidth={3} />}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Nombre */}
                    {h.nombre && (
                      <p className="font-bold text-gray-900 text-sm mb-2">{h.nombre}</p>
                    )}

                    {/* Bloques */}
                    {h.bloques?.length > 0 ? (
                      <div className="space-y-2">
                        {h.bloques.map((blq, bi) => (
                          <div key={bi} className="flex flex-wrap items-center gap-2">
                            {/* Días */}
                            <div className="flex gap-1">
                              {blq.dias?.map((d, di) => (
                                <span key={di}
                                  className={`text-[11px] font-bold px-2 py-0.5 rounded-full
                                    ${sel ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                  {diaShort(d)}
                                </span>
                              ))}
                            </div>
                            {/* Hora */}
                            <span className={`inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-0.5 rounded-full
                              ${sel ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                              <FiClock size={11} /> {blq.inicio} – {blq.fin}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className={`inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-0.5 rounded-full
                        ${sel ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                        <FiClock size={11} />
                        {h.dia || h.dias?.join(', ')} · {h.inicio} – {h.fin}
                      </span>
                    )}

                    {/* Cupos */}
                    {!ilimitado && h.cupos > 0 && (
                      <div className="mt-3">
                        <div className="cupos-bar">
                          <div className="cupos-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
                        </div>
                        <p className="text-[11px] mt-1 font-medium" style={{ color: barColor }}>
                          {sobrecupo
                            ? `Sobrecupo: ${ocupados}/${total} inscritos`
                            : disp <= 0
                              ? 'Sin cupos disponibles'
                              : disp <= 5
                                ? `¡Solo ${disp} cupos!`
                                : `${disp} de ${total} disponibles`}
                        </p>
                      </div>
                    )}
                    {ilimitado && (
                      <p className="text-[11px] text-blue-600 font-medium mt-2">Cupos ilimitados</p>
                    )}
                  </div>
                </div>
              </button>
            );
          }) : (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <FiAlertTriangle size={28} className="text-gray-300" />
              <p className="text-gray-500 text-sm font-medium">Sin horarios configurados</p>
            </div>
          )}

          {/* Todos llenos */}
          {taller.horarios?.length > 0 &&
            taller.horarios.every(h => !h.cuposIlimitados && ((h.cupos || 0) - (h.cuposOcupados || 0) - (h.cuposReservados || 0)) <= 0) && (
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-red-50 border border-red-100">
                <FiAlertTriangle size={18} className="text-red-500 flex-shrink-0" />
                <p className="text-sm font-semibold text-red-700">Todos los horarios están llenos</p>
              </div>
            )}
        </div>

        {/* CTA */}
        <div className="sticky bottom-0 bg-white -mx-4 px-4 pb-2 pt-3 border-t border-gray-100 sm:static sm:border-0 sm:p-0 sm:bg-transparent">
          <button
            onClick={onNext}
            disabled={selectedHorarios.length === 0}
            className="w-full sm:w-auto sm:ml-auto flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-bold text-sm text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-105 active:scale-[.98]"
            style={{ background: '#0047bb' }}
          >
            Continuar
            {selectedHorarios.length > 0 && (
              <span className="bg-white/20 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">
                {selectedHorarios.length}
              </span>
            )}
            <FiArrowRight size={16} />
          </button>
        </div>
      </div>
    </ConfigProvider>
  );
}
